import { useState } from 'react'

import { Badge, type Tone } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Field, Input, Select, Textarea } from '@/components/ui/Field'
import { Page, PageHeader, Section } from '@/components/ui/Page'
import { EmptyState, ErrorState, Loading } from '@/components/ui/States'
import { formatDateShort } from '@/lib/dates'
import { pluralise } from '@/lib/format'
import { describeError } from '@/lib/supabase'
import {
  CAMPAIGN_KIND_LABEL,
  CAMPAIGN_STATUS_LABEL,
  useCampaigns,
  useDeleteCampaign,
  useSaveCampaign,
  type Campaign,
} from './queries'

const STATUS_TONE: Record<Campaign['status'], Tone> = { idea: 'neutral', planned: 'amber', running: 'sage', done: 'ink' }

/**
 * Campaigns and promotions (27 Oct 2026): seasonal offers, mini sessions,
 * vendor outreach and ad campaigns, from an idea to done. The monthly
 * promotions check-in and the planner reminder feed this list.
 */
export function CampaignsPage() {
  const campaigns = useCampaigns()
  const [editing, setEditing] = useState<Campaign | 'new' | null>(null)
  const order: Campaign['status'][] = ['running', 'planned', 'idea', 'done']

  return (
    <Page>
      <PageHeader
        eyebrow="Marketing"
        title="Campaigns"
        meta={campaigns.data ? <span>{pluralise(campaigns.data.filter((c) => c.status === 'running').length, 'running now', 'running now')}</span> : null}
        actions={
          <Button variant="primary" onClick={() => setEditing('new')}>
            Add a campaign
          </Button>
        }
      />

      {editing ? <CampaignForm key={editing === 'new' ? 'new' : editing.id} campaign={editing === 'new' ? null : editing} onDone={() => setEditing(null)} /> : null}

      {campaigns.isPending ? (
        <Loading label="Loading campaigns" />
      ) : campaigns.isError ? (
        <ErrorState title="Campaigns could not load" detail={describeError(campaigns.error)} onRetry={() => void campaigns.refetch()} />
      ) : campaigns.data.length === 0 ? (
        <EmptyState
          title="No campaigns yet"
          body="Write down promotions, mini sessions, vendor outreach and ad campaigns, from a first idea to done."
          action={{ label: 'Add a campaign', onClick: () => setEditing('new') }}
        />
      ) : (
        order.map((status) => {
          const rows = campaigns.data.filter((campaign) => campaign.status === status)
          if (rows.length === 0) return null
          return (
            <section key={status} className="mb-5">
              <h2 className="mb-2 text-[length:var(--text-base)] font-semibold">
                {CAMPAIGN_STATUS_LABEL[status] ?? status} <span className="text-taupe">({rows.length})</span>
              </h2>
              <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {rows.map((campaign) => (
                  <li key={campaign.id}>
                    <button type="button" onClick={() => setEditing(campaign)} className="card block w-full p-4 text-left hover:shadow-[var(--shadow-menu)]">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold">{campaign.name}</p>
                        <Badge tone={STATUS_TONE[campaign.status] ?? 'neutral'}>{CAMPAIGN_KIND_LABEL[campaign.kind] ?? campaign.kind}</Badge>
                      </div>
                      {campaign.starts_on ? (
                        <p className="mt-1 text-[length:var(--text-2xs)] text-taupe">
                          {formatDateShort(campaign.starts_on)}
                          {campaign.ends_on ? ` to ${formatDateShort(campaign.ends_on)}` : ''}
                        </p>
                      ) : null}
                      {campaign.offer ? <p className="mt-2 text-[length:var(--text-sm)]">{campaign.offer}</p> : null}
                      {campaign.audience ? <p className="mt-1 text-[length:var(--text-2xs)] text-taupe">For {campaign.audience}</p> : null}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )
        })
      )}
    </Page>
  )
}

function CampaignForm({ campaign, onDone }: { campaign: Campaign | null; onDone: () => void }) {
  const save = useSaveCampaign()
  const remove = useDeleteCampaign()
  const [form, setForm] = useState({
    name: campaign?.name ?? '',
    kind: campaign?.kind ?? 'promotion',
    status: campaign?.status ?? 'idea',
    starts_on: campaign?.starts_on ?? '',
    ends_on: campaign?.ends_on ?? '',
    offer: campaign?.offer ?? '',
    audience: campaign?.audience ?? '',
    notes: campaign?.notes ?? '',
  })
  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm((current) => ({ ...current, [key]: value }))

  return (
    <Section title={campaign ? campaign.name : 'Add a campaign'}>
      <form
        className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4"
        onSubmit={(event) => {
          event.preventDefault()
          if (!form.name.trim()) return
          save.mutate(
            {
              id: campaign?.id ?? null,
              input: {
                name: form.name.trim(),
                kind: form.kind,
                status: form.status,
                starts_on: form.starts_on || null,
                ends_on: form.ends_on || null,
                offer: form.offer.trim() || null,
                audience: form.audience.trim() || null,
                notes: form.notes.trim() || null,
              },
            },
            { onSuccess: onDone },
          )
        }}
      >
        <Field label="Name" className="sm:col-span-2">
          <Input value={form.name} onChange={(event) => set('name', event.target.value)} placeholder="Winter wedding offer" />
        </Field>
        <Field label="Kind">
          <Select value={form.kind} onChange={(event) => set('kind', event.target.value as Campaign['kind'])}>
            {Object.entries(CAMPAIGN_KIND_LABEL).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Status">
          <Select value={form.status} onChange={(event) => set('status', event.target.value as Campaign['status'])}>
            {Object.entries(CAMPAIGN_STATUS_LABEL).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Starts">
          <Input type="date" value={form.starts_on} onChange={(event) => set('starts_on', event.target.value)} />
        </Field>
        <Field label="Ends">
          <Input type="date" value={form.ends_on} onChange={(event) => set('ends_on', event.target.value)} />
        </Field>
        <Field label="For whom" className="sm:col-span-2">
          <Input value={form.audience} onChange={(event) => set('audience', event.target.value)} placeholder="Engaged couples, planners, past clients ..." />
        </Field>
        <Field label="The offer" className="sm:col-span-2">
          <Textarea rows={3} value={form.offer} onChange={(event) => set('offer', event.target.value)} />
        </Field>
        <Field label="Notes" className="sm:col-span-2">
          <Textarea rows={3} value={form.notes} onChange={(event) => set('notes', event.target.value)} />
        </Field>
        <div className="flex flex-wrap items-center gap-2 sm:col-span-2 lg:col-span-4">
          <Button type="submit" variant="primary" loading={save.isPending} loadingLabel="Saving">
            {campaign ? 'Save' : 'Add campaign'}
          </Button>
          <Button variant="ghost" onClick={onDone}>
            Cancel
          </Button>
          {campaign ? (
            <Button
              variant="ghost"
              className="ml-auto text-clay"
              onClick={() => {
                if (window.confirm(`Remove "${campaign.name}"?`)) remove.mutate(campaign.id, { onSuccess: onDone })
              }}
            >
              Remove
            </Button>
          ) : null}
        </div>
      </form>
    </Section>
  )
}
